import React, { FC, HTMLProps, PropsWithChildren, ReactElement, ReactNode } from 'react';
import { SubmitHandler, UseFormReturn, useForm } from 'react-hook-form';

import Field from './Field';

interface Props extends Omit<HTMLProps<HTMLFormElement>, 'onSubmit' | 'form' | 'onChange'> {
  onSubmit: SubmitHandler<any>;
  form: UseFormReturn<any, any>;
  onChange?: (values: any, field: string, value: any, prev: any) => void;
}

type FormType = FC<PropsWithChildren<Props>> & { useForm: typeof useForm };

const Form: FormType = ({ children: childrenProp, onSubmit, form, onChange, ...rest }) => {
  const {
    handleSubmit,
    formState: { errors },
    control,
  } = form;
  const onFieldChange = (field: string, prevValue: any) => {
    onChange?.(form.getValues(), field, form.getValues(field), prevValue);
  };

  const renderRecursiveChildren = (children: ReactNode): ReactNode => {
    return React.Children.map(children as JSX.Element, (child: ReactElement) => {
      if (!!child) {
        if (
          typeof child.type !== 'string' &&
          (child.type as typeof Field)?.displayName === 'Field' &&
          child.props.name
        ) {
          return React.createElement(child.type, {
            ...{
              error: errors[child.props.name]?.message,
              ...child.props,
              register: form.register,
              key: child.props.name,
              onFieldChange,
              control,
            },
          });
        }
        if (child?.props?.children) {
          return React.createElement(child.type, {
            ...{
              ...child.props,
              children: renderRecursiveChildren(child.props.children),
            },
          });
        }
        return child;
      } else {
        return null;
      }
    });
  };
  return (
    <form onSubmit={handleSubmit(onSubmit)} {...rest}>
      {renderRecursiveChildren(childrenProp)}
    </form>
  );
};

Form.useForm = (props = {}) => useForm({ mode: 'onChange', ...props });

export default Form;
